import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, Users, FileText, GraduationCap, BookOpen, Calendar, Grid, Settings,
  CheckCircle, Plus, Edit
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import { useAuth } from "../contexts";
import { apiCall } from "../lib/api";

export const AdminDashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ students: 0, teachers: 0, parents: 0, announcements: 0 });
  const [users, setUsers] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [showCreateAnnouncement, setShowCreateAnnouncement] = useState(false);
  const [announcementForm, setAnnouncementForm] = useState({ title: "", content: "", category: "General", priority: "normal" });
  const [submitting, setSubmitting] = useState(false);
  const [courseTeachers, setCourseTeachers] = useState({});
  const [editingCourse, setEditingCourse] = useState(null);

  useEffect(() => {
    if (!user) { navigate("/portal"); return; }
    if (user.user_type !== "admin") { navigate("/dashboard"); return; }
    fetchData();
    const savedTeachers = localStorage.getItem("courseTeachers");
    if (savedTeachers) setCourseTeachers(JSON.parse(savedTeachers));
  }, [user, navigate]);

  const saveCourseTeacher = (courseName, teacherName) => {
    const updated = { ...courseTeachers, [courseName]: teacherName };
    setCourseTeachers(updated);
    localStorage.setItem("courseTeachers", JSON.stringify(updated));
    setEditingCourse(null);
    toast.success("Teacher assigned successfully!");
  };

  const fetchData = async () => {
    try {
      const [usersRes, announcementsRes, eventsRes] = await Promise.all([
        apiCall("GET", "/users", null, token),
        apiCall("GET", "/announcements", null, token),
        apiCall("GET", "/events", null, token)
      ]);
      setUsers(usersRes);
      setAnnouncements(announcementsRes);
      setEvents(eventsRes);
      setStats({
        students: usersRes.filter(u => u.user_type === "student").length,
        teachers: usersRes.filter(u => u.user_type === "teacher").length,
        parents: usersRes.filter(u => u.user_type === "parent").length,
        announcements: announcementsRes.length
      });
    } catch (err) { console.error("Failed to fetch data:", err); }
    setLoading(false);
  };

  const handleCreateAnnouncement = async () => {
    if (!announcementForm.title || !announcementForm.content) { toast.error("Please fill in all required fields"); return; }
    setSubmitting(true);
    try {
      await apiCall("POST", "/announcements", announcementForm, token);
      toast.success("Announcement created successfully!");
      setShowCreateAnnouncement(false);
      setAnnouncementForm({ title: "", content: "", category: "General", priority: "normal" });
      fetchData();
    } catch (err) { toast.error("Failed to create announcement"); }
    setSubmitting(false);
  };

  const getPriorityBadge = (priority) => {
    const styles = { high: "bg-red-100 text-red-600", normal: "bg-blue-100 text-blue-600", low: "bg-green-100 text-green-600" };
    return styles[priority] || styles.normal;
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="spinner"></div></div>;

  const courses = [
    { name: "Mathematics", room: "Room 101", schedule: "Mon/Wed/Fri 8:00 AM" },
    { name: "English Language", room: "Room 102", schedule: "Mon/Wed/Fri 9:00 AM" },
    { name: "Integrated Science", room: "Lab 201", schedule: "Tue/Thu 8:00 AM" },
    { name: "Social Studies", room: "Room 103", schedule: "Tue/Thu 10:00 AM" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8 rounded-2xl overflow-hidden shadow-lg">
          <div className="grid grid-cols-3 h-48">
            <img src="https://customer-assets.emergentagent.com/job_prep-dashboard-5/artifacts/dfxf5l60_WhatsApp%20Image%202026-01-20%20at%206.40.17%20AM%20%282%29.jpeg" alt="Graduation" className="w-full h-full object-cover" />
            <img src="https://customer-assets.emergentagent.com/job_prep-dashboard-5/artifacts/5w9qtq4m_WhatsApp%20Image%202026-01-20%20at%206.48.57%20AM.jpeg" alt="Library" className="w-full h-full object-cover" />
            <img src="https://customer-assets.emergentagent.com/job_prep-dashboard-5/artifacts/5hv6l85o_WhatsApp%20Image%202026-01-20%20at%206.49.00%20AM%20%281%29%20-%20Copy.jpeg" alt="Event" className="w-full h-full object-cover" />
          </div>
        </div>

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-[#002147] rounded-full flex items-center justify-center"><Settings className="text-white" size={24} /></div>
            <div>
              <h1 className="text-3xl font-bold text-[#002147]">Administration Dashboard</h1>
              <p className="text-[#002147]/70">Welcome, {user?.first_name} - Young Christian Preparatory School/ JHS</p>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-white card-hover"><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-500">Total Students</p><p className="text-3xl font-bold text-[#002147]">{stats.students}</p></div><div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center"><GraduationCap className="text-blue-600" size={24} /></div></div></CardContent></Card>
          <Card className="bg-white card-hover"><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-500">Total Teachers</p><p className="text-3xl font-bold text-[#002147]">{stats.teachers}</p></div><div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center"><BookOpen className="text-green-600" size={24} /></div></div></CardContent></Card>
          <Card className="bg-white card-hover"><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-500">Total Parents</p><p className="text-3xl font-bold text-[#002147]">{stats.parents}</p></div><div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center"><Users className="text-purple-600" size={24} /></div></div></CardContent></Card>
          <Card className="bg-white card-hover"><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-500">Announcements</p><p className="text-3xl font-bold text-[#002147]">{stats.announcements}</p></div><div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center"><Bell className="text-orange-600" size={24} /></div></div></CardContent></Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="users">Manage Users</TabsTrigger>
            <TabsTrigger value="announcements">Announcements</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <Card className="mb-6 bg-white">
              <CardHeader><CardTitle className="flex items-center gap-2"><Grid size={20} /> Quick Links</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                  <Button variant="outline" onClick={() => navigate("/resources")} className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-[#002147] hover:text-white"><BookOpen size={24} /><span className="text-sm font-medium">Academics</span></Button>
                  <Button variant="outline" onClick={() => navigate("/staff")} className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-[#002147] hover:text-white"><Users size={24} /><span className="text-sm font-medium">Faculty Directory</span></Button>
                  <Button variant="outline" onClick={() => navigate("/events")} className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-[#002147] hover:text-white"><Calendar size={24} /><span className="text-sm font-medium">Calendar</span></Button>
                  <Button variant="outline" onClick={() => navigate("/announcements")} className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-[#002147] hover:text-white"><Bell size={24} /><span className="text-sm font-medium">News</span></Button>
                  <Button onClick={() => navigate("/fees")} className="bg-[#FF6B00] hover:bg-[#E65C00] h-auto py-4 flex flex-col items-center gap-2"><FileText size={24} /><span className="text-sm font-medium">Pay Fees</span></Button>
                </div>
              </CardContent>
            </Card>

            <Card className="mb-6 bg-white">
              <CardHeader><CardTitle className="flex items-center gap-2"><BookOpen size={20} /> School Courses</CardTitle></CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
                  {courses.map((course, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg border-l-4 border-[#FF6B00]">
                      <h4 className="font-semibold text-[#002147]">{course.name}</h4>
                      <p className="text-xs text-gray-500">{course.schedule}</p>
                      <Badge variant="outline" className="mt-1">{course.room}</Badge>
                      <div className="mt-2 pt-2 border-t">
                        {editingCourse === course.name ? (
                          <div className="flex gap-2">
                            <Input placeholder="Enter teacher name" defaultValue={courseTeachers[course.name]} className="h-8 text-sm" onKeyDown={(e) => { if (e.key === 'Enter') saveCourseTeacher(course.name, e.target.value); }} autoFocus />
                            <Button size="sm" className="h-8 bg-green-600 hover:bg-green-700" onClick={(e) => { const input = e.target.parentElement.querySelector('input'); saveCourseTeacher(course.name, input.value); }}><CheckCircle size={14} /></Button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">{courseTeachers[course.name] || <span className="italic text-gray-400">No teacher assigned</span>}</span>
                            <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => setEditingCourse(course.name)}><Edit size={12} /></Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="bg-white">
                <CardHeader><CardTitle className="flex items-center gap-2"><Users size={20} /> Recent Users</CardTitle></CardHeader>
                <CardContent>
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-3">
                      {users.slice(0, 10).map((u) => (
                        <div key={u.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Avatar><AvatarFallback className="bg-[#002147] text-white">{u.first_name?.[0]}{u.last_name?.[0]}</AvatarFallback></Avatar>
                            <div><p className="font-medium">{u.first_name} {u.last_name}</p><p className="text-xs text-gray-500">{u.email}</p></div>
                          </div>
                          <Badge variant="outline" className="capitalize">{u.user_type}</Badge>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2"><Bell size={20} /> Recent Announcements</CardTitle>
                  <Button size="sm" onClick={() => setShowCreateAnnouncement(true)} className="bg-[#FF6B00] hover:bg-[#E65C00]"><Plus size={14} className="mr-1" /> New</Button>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-3">
                      {announcements.slice(0, 5).map((a) => (
                        <div key={a.id} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2 mb-1"><Badge className={getPriorityBadge(a.priority)}>{a.priority}</Badge><Badge variant="outline">{a.category}</Badge></div>
                          <h4 className="font-medium">{a.title}</h4>
                          <p className="text-sm text-gray-500 line-clamp-2">{a.content}</p>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="users">
            <Card className="bg-white">
              <CardHeader><CardTitle>All Users</CardTitle><CardDescription>Manage students, teachers, parents, and administrators</CardDescription></CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {users.map((u) => (
                      <div key={u.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Avatar><AvatarFallback className={`text-white ${u.user_type === 'admin' ? 'bg-[#FF6B00]' : u.user_type === 'teacher' ? 'bg-green-600' : u.user_type === 'parent' ? 'bg-purple-600' : 'bg-[#002147]'}`}>{u.first_name?.[0]}{u.last_name?.[0]}</AvatarFallback></Avatar>
                          <div><p className="font-medium">{u.first_name} {u.last_name}</p><p className="text-xs text-gray-500">{u.email}</p></div>
                        </div>
                        <Badge variant="outline" className={`capitalize ${u.user_type === 'admin' ? 'border-[#FF6B00] text-[#FF6B00]' : ''}`}>{u.user_type}</Badge>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="announcements">
            <Card className="bg-white">
              <CardHeader className="flex flex-row items-center justify-between">
                <div><CardTitle>Manage Announcements</CardTitle><CardDescription>Create and manage school announcements</CardDescription></div>
                <Button onClick={() => setShowCreateAnnouncement(true)} className="bg-[#FF6B00] hover:bg-[#E65C00]"><Plus size={16} className="mr-2" /> Create</Button>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-4">
                    {announcements.map((a) => (
                      <Card key={a.id} className="border-l-4 border-l-[#FF6B00]">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-2"><Badge className={getPriorityBadge(a.priority)}>{a.priority}</Badge><Badge variant="outline">{a.category}</Badge></div>
                          <h3 className="text-lg font-semibold text-[#002147]">{a.title}</h3>
                          <p className="text-gray-600">{a.content}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={showCreateAnnouncement} onOpenChange={setShowCreateAnnouncement}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Bell size={20} /> Create Announcement</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title *</Label><Input value={announcementForm.title} onChange={(e) => setAnnouncementForm({...announcementForm, title: e.target.value})} placeholder="Title" /></div>
            <div><Label>Content *</Label><Textarea rows={4} value={announcementForm.content} onChange={(e) => setAnnouncementForm({...announcementForm, content: e.target.value})} placeholder="Content..." /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Category</Label><Select value={announcementForm.category} onValueChange={(v) => setAnnouncementForm({...announcementForm, category: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="General">General</SelectItem><SelectItem value="Academic">Academic</SelectItem><SelectItem value="Sports">Sports</SelectItem></SelectContent></Select></div>
              <div><Label>Priority</Label><Select value={announcementForm.priority} onValueChange={(v) => setAnnouncementForm({...announcementForm, priority: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="normal">Normal</SelectItem><SelectItem value="high">High</SelectItem></SelectContent></Select></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCreateAnnouncement(false)}>Cancel</Button><Button className="bg-[#FF6B00] hover:bg-[#E65C00]" onClick={handleCreateAnnouncement} disabled={submitting}>{submitting ? "Creating..." : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
